/**
 * How the refusal rate reads, including when there is nothing to rate.
 *
 * The card showed `(refusal_rate * 100).toFixed(1)` over a statistic that
 * counted every decision ever recorded, under the caption "In the last hour".
 * Measured on production 2026-09-04: 47.3%, unchanged across hours, over a
 * Decisions page holding about fifteen rows a day. It was read as a live
 * refusal rate and cost somebody a scare.
 *
 * The label was the smaller half. A rate over all history cannot show a spike,
 * which is the one thing the card is for, and it gets more stable as the table
 * grows, which reads as more trustworthy.
 *
 * With a real window comes a real empty case, and 0.0% is the wrong way to
 * render it: it says nothing is being refused, when what happened is that
 * nothing was presented. On a quiet night that is most hours, so the card
 * would spend most of its life saying something reassuring and false.
 */

export interface RefusalReading {
  value: string
  unit?: string
  caption: string
}

export function refusalReading(
  rate: number | null | undefined,
  decisions: number,
): RefusalReading {
  if (rate === null || rate === undefined || decisions === 0) {
    return {
      value: 'none',
      caption: 'No authorization has been presented in this window',
    }
  }
  return {
    value: (rate * 100).toFixed(1),
    unit: '%',
    caption: `Of the ${decisions} decision${decisions === 1 ? '' : 's'} in this window`,
  }
}


/**
 * The refusal rate over a whole range, for the card above the chart.
 *
 * Counted from the same answer the chart is drawn from, so the card and the
 * line cannot disagree. Buckets with nothing in them carry null and are left
 * out of both halves: including them as zero would drag the rate down by
 * however many quiet hours the range happens to contain, which would make a
 * long quiet range look safer than a short busy one.
 */
export function rangeRefusal(
  series: { decisions: { by_outcome: Record<string, number[]>; total: number } } | null,
): { rate: number | null; total: number } | null {
  if (!series) return null
  const total = series.decisions.total
  if (total === 0) return { rate: null, total: 0 }
  const approved = (series.decisions.by_outcome.APPROVED ?? []).reduce((a, b) => a + b, 0)
  return { rate: (total - approved) / total, total }
}
